import type { Annotation, Asset, NormRect, Step } from './demo';

/**
 * Focus regions — the system that makes "correct on every screen" true rather
 * than aspirational (PRODUCT-PLAN §3, DESIGN-SYSTEM §0).
 *
 * The problem: a 1440px desktop screenshot rendered at 390px is not a mobile
 * demo, it is an unreadable postage stamp. Pixels scale; legibility does not.
 * Incumbents' HTML demos *break* on phones; naive image demos are merely *tiny*.
 * Both fail the viewer.
 *
 * The fix: below the width at which the asset's own text stays legible, the
 * player stops fitting the whole frame and instead frames the step's region of
 * interest, panning between steps. Because that region is derived from the
 * hotspot geometry, ~80% of steps never author it — which is what keeps the
 * feature cheap.
 *
 * This module is shared by player AND editor on purpose: the derivation rule
 * must be byte-identical in both, or the editor preview lies.
 */

/** Padding added around the union of hotspots when deriving a focus rect. */
export const FOCUS_PAD = 0.12;
/**
 * Guards against a degenerate focus: a 4px button must not imply a 20x zoom.
 * These are NOT "keep this much context" minimums — context is decided at layout
 * time, where the viewport is known, not at derivation time, where it is not.
 */
export const FOCUS_MIN_W = 0.2;
export const FOCUS_MIN_H = 0.08;
/** Below this rendered height, text is not comfortably readable. */
export const MIN_TEXT_PX = 11;
/**
 * Hysteresis. We escalate to focus mode only below MIN_TEXT_PX * this, but once
 * escalated we zoom all the way to MIN_TEXT_PX. Without the gap, a viewport
 * hovering near the threshold — someone dragging a browser edge, or a phone
 * rotating — would flip between fitted and zoomed on every resize frame, which
 * looks broken. The gap also spares borderline-but-readable desktop embeds from
 * zooming for a 5% shortfall.
 */
export const ESCALATE_MARGIN = 0.9;
/**
 * Fallback when the OCR pass produced no measurement. ~1.4% of asset height
 * matches both a 1170x2532 phone capture (17pt body ≈ 34px) and a 1440x900
 * desktop capture (14px body), so it is a safe default for either source.
 */
export const DEFAULT_MEDIAN_TEXT_H = 0.014;
/**
 * Absolute zoom ceiling relative to fit-scale. 4x is what a 1440px-wide desktop
 * capture needs to reach legible text on a 390px phone; beyond that the viewer
 * has lost the plot and a paired mobile capture is the real answer.
 */
export const MAX_FOCUS_ZOOM = 4;
/**
 * The smallest zoom worth doing. A desktop capture on a laptop-sized box sits
 * just under the legibility line; zooming 1.1x there buys a pixel of text
 * height and costs the screen's edges ("…nvoices" instead of "Invoices"), which
 * reads as a cropping bug, not help. Focus mode is for phones, where the
 * needed zoom is 2–4x; below this, fit the whole frame.
 */
export const MIN_FOCUS_ZOOM = 1.3;
/**
 * "Show whole screen" (the editor's camera choice, `focus` = the whole frame)
 * under a legibility zoom: at least this share of the screen's width and of
 * its height stays in the picture, counted from the top-left corner, and the
 * zoom is anchored there.
 *
 * Why the top-left: an app's main navigation runs along the top (a 48–64 px
 * bar on a 900 px screen, 5–7 %) and/or down the left (a 200–280 px sidebar
 * on a 1440 px screen, 14–20 %) — Studio's own editor keeps its step list
 * there too. Anchored at that corner a zoom never cuts the navigation off,
 * which is what made a "whole screen" stop reading as the screen (found on
 * mado's own demo: a 1.63x zoom centred on "Write with AI" lost the step
 * list). The step's targets (`lookAt`) are kept in view as well, so a target
 * near the right or bottom edge lowers the zoom further rather than sliding
 * the picture off the navigation.
 *
 * Why 0.72: it caps the zoom at 1/0.72 = 1.39x, a little above
 * `MIN_FOCUS_ZOOM` — enough for a zoom to be worth doing (a 1080 px video of
 * a 2880 px capture reads 1.39/1.63 of the way to `MIN_TEXT_PX` instead of
 * not at all), while the screen's whole working area next to the navigation
 * (a list, a form, the editor's canvas) is still on show. Past it the author
 * asked for the whole screen and got a close-up. A zoom this rule caps below
 * `MIN_FOCUS_ZOOM` is not done: the step shows the whole screen, as asked.
 */
export const WHOLE_SCREEN_MIN_SHOWN = 0.72;

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** Smallest rect containing all inputs. Returns null for an empty list. */
export function unionRects(rects: NormRect[]): NormRect | null {
  if (rects.length === 0) return null;
  let x0 = 1;
  let y0 = 1;
  let x1 = 0;
  let y1 = 0;
  for (const r of rects) {
    if (r.x < x0) x0 = r.x;
    if (r.y < y0) y0 = r.y;
    if (r.x + r.w > x1) x1 = r.x + r.w;
    if (r.y + r.h > y1) y1 = r.y + r.h;
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** Grow a rect about its centre, clamped to the unit square. */
export function padRect(r: NormRect, pad: number): NormRect {
  return growTo(r, r.w + pad * 2, r.h + pad * 2);
}

/** Grow (never shrink) a rect about its centre to at least w x h, clamped to 0..1. */
export function growTo(r: NormRect, w: number, h: number): NormRect {
  const nw = clamp(Math.max(r.w, w), 0, 1);
  const nh = clamp(Math.max(r.h, h), 0, 1);
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  return {
    x: clamp(cx - nw / 2, 0, 1 - nw),
    y: clamp(cy - nh / 2, 0, 1 - nh),
    w: nw,
    h: nh,
  };
}

function annotationRect(a: Annotation | null): NormRect | null {
  if (!a) return null;
  return typeof a.anchor === 'string' ? null : a.anchor;
}

/**
 * Resolve a step's focus region: the authored rect if present, otherwise derived
 * from what the step actually asks the viewer to look at.
 *
 * Returns null when there is nothing to focus on (a step with no hotspots and no
 * anchored annotation) — the player then keeps the whole frame.
 */
export function resolveFocus(step: Step): NormRect | null {
  if (step.focus) return step.focus;

  const rects: NormRect[] = step.hotspots.map((h) => h.geom);
  const ar = annotationRect(step.annotation);
  if (ar) rects.push(ar);

  const base = unionRects(rects);
  if (!base) return null;

  return growTo(padRect(base, FOCUS_PAD), FOCUS_MIN_W, FOCUS_MIN_H);
}

/* ── layout ─────────────────────────────────────────────────────────────── */

export interface Viewport {
  w: number;
  h: number;
}

export interface LayoutInput {
  viewport: Viewport;
  asset: Pick<Asset, 'w' | 'h'> & {
    medianTextH?: number | undefined;
    /** Where the picture has content (`Asset.ink`): the camera's frames cut only between it. */
    ink?: Asset['ink'] | undefined;
  };
  /** Already resolved via resolveFocus(). */
  focus: NormRect | null;
  /**
   * False when focusing would be pointless or harmful — notably a native phone
   * capture shown on a phone, which is already pixel-exact at fit scale.
   */
  allowFocus?: boolean;
  /**
   * What a legibility zoom must keep in view when `focus` is the whole frame
   * (the editor's "Show whole screen"): the step's targets (`spotlightRect`).
   * That zoom is anchored at the top-left and capped so these stay in the
   * picture (`WHOLE_SCREEN_MIN_SHOWN`); without it a target near an edge —
   * the "New demo" button top right — could end up outside the picture,
   * where nobody can click it.
   */
  lookAt?: NormRect | null;
}

export interface Layout {
  mode: 'contain' | 'focus';
  /** Multiplier from asset pixels to CSS pixels. */
  scale: number;
  /** Translation in CSS px applied to the scaled asset layer. */
  tx: number;
  ty: number;
  /** The scale at which the whole asset fits — useful for zoom-out affordances. */
  containScale: number;
  /** Rendered height of median body text at `scale`, for diagnostics/tests. */
  textPx: number;
}

/**
 * Compute how the asset should sit in the viewport for this step.
 *
 * Only `scale`, `tx` and `ty` ever reach the DOM, and only through a single
 * `transform` — never width/height/top/left. That is what keeps the pan at 60fps
 * (CLAUDE.md / DESIGN-SYSTEM §5 rule 1).
 */
export function computeLayout(input: LayoutInput): Layout {
  const { viewport: vp, asset, focus } = input;
  const allowFocus = input.allowFocus ?? true;

  const containScale = Math.min(vp.w / asset.w, vp.h / asset.h);
  const medianTextH = asset.medianTextH ?? DEFAULT_MEDIAN_TEXT_H;
  const textAtContain = medianTextH * asset.h * containScale;

  const centred = (scale: number): Layout => {
    const sw = asset.w * scale;
    const sh = asset.h * scale;
    return {
      mode: 'contain',
      scale,
      tx: (vp.w - sw) / 2,
      ty: (vp.h - sh) / 2,
      containScale,
      textPx: medianTextH * asset.h * scale,
    };
  };

  // Legible as-is, or nothing to focus on, or focusing is disallowed: fit it.
  // A native phone capture on a phone lands here — it is already pixel-exact,
  // which is why "perfect on mobile" costs nothing for mobile-sourced demos.
  if (!allowFocus || !focus || textAtContain >= MIN_TEXT_PX * ESCALATE_MARGIN) {
    return centred(containScale);
  }

  // Zoom to exactly the point where body text becomes legible and no further:
  // every extra bit of zoom trades away context for no readability gain. The
  // focus rect decides *where* we look, never *how far* we zoom.
  const neededScale = MIN_TEXT_PX / (medianTextH * asset.h);
  const scale = clamp(neededScale, containScale, containScale * MAX_FOCUS_ZOOM);

  if (scale < containScale * MIN_FOCUS_ZOOM) return centred(containScale);

  const wholeFrame = focus.w >= 0.98 && focus.h >= 0.98;
  if (wholeFrame) {
    // "Show whole screen": anchored at the top-left, never past
    // `WHOLE_SCREEN_MIN_SHOWN`, the targets kept in view (see the constant).
    const look = input.lookAt;
    const right = Math.max(WHOLE_SCREEN_MIN_SHOWN, look ? look.x + look.w : 0);
    const bottom = Math.max(WHOLE_SCREEN_MIN_SHOWN, look ? look.y + look.h : 0);
    const whole = Math.min(scale, vp.w / (asset.w * right), vp.h / (asset.h * bottom));
    if (whole < containScale * MIN_FOCUS_ZOOM) return centred(containScale);
    const ww = asset.w * whole;
    const wh = asset.h * whole;
    return {
      mode: 'focus',
      scale: whole,
      tx: ww <= vp.w ? (vp.w - ww) / 2 : 0,
      ty: wh <= vp.h ? (vp.h - wh) / 2 : 0,
      containScale,
      textPx: medianTextH * asset.h * whole,
    };
  }

  const sw = asset.w * scale;
  const sh = asset.h * scale;
  const look = focus;
  const cx = (look.x + look.w / 2) * asset.w * scale;
  const cy = (look.y + look.h / 2) * asset.h * scale;

  // Centre the focus rect, then pull back so we never reveal empty space beside
  // the asset. If the asset is smaller than the viewport on an axis, centre it.
  const tx = sw <= vp.w ? (vp.w - sw) / 2 : clamp(vp.w / 2 - cx, vp.w - sw, 0);
  const ty = sh <= vp.h ? (vp.h - sh) / 2 : clamp(vp.h / 2 - cy, vp.h - sh, 0);

  return { mode: 'focus', scale, tx, ty, containScale, textPx: medianTextH * asset.h * scale };
}

/** Map a pointer position in viewport CSS px to normalized asset coordinates. */
export function viewportToAsset(
  point: { x: number; y: number },
  layout: Layout,
  asset: Pick<Asset, 'w' | 'h'>,
): { x: number; y: number } {
  return {
    x: (point.x - layout.tx) / (asset.w * layout.scale),
    y: (point.y - layout.ty) / (asset.h * layout.scale),
  };
}

/** Is a normalized point inside a hotspot of the given shape? */
export function hitTest(
  point: { x: number; y: number },
  geom: NormRect,
  shape: 'rect' | 'ellipse',
): boolean {
  if (shape === 'rect') {
    return (
      point.x >= geom.x &&
      point.x <= geom.x + geom.w &&
      point.y >= geom.y &&
      point.y <= geom.y + geom.h
    );
  }
  const rx = geom.w / 2;
  const ry = geom.h / 2;
  const dx = (point.x - (geom.x + rx)) / rx;
  const dy = (point.y - (geom.y + ry)) / ry;
  return dx * dx + dy * dy <= 1;
}
